import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CountryService } from '../services/country.service';
import { Country, Region } from '../models/country.model';
import { FormDataService } from '../services/form-data.service';
import { Router } from '@angular/router';
import { ClientServiceService } from '../services/client-service.service';
import { AuthServiceService } from '../services/auth-service.service';
import { TranslateService } from '@ngx-translate/core';


const error_message_types = {
  phone_invalid: "Invalid phone number",
  email_invalid: (email: string) => `Authorization form with email ${email} already exist`
}

@Component({
  selector: 'app-personal-info-form',
  templateUrl: './personal-info-form.component.html',
  styleUrls: ['./personal-info-form.component.css']
})
export class PersonalInfoFormComponent implements OnInit{

  countries: Country[] = [];
  selectedCountryRegions: Region[] = [];
  currentCountryCode: string = "";

  @Output() infoSubmit = new EventEmitter<void>();
  @Output() rerender = new EventEmitter<void>();
  @Output() signatureSubmit = new EventEmitter<void>();

  @ViewChild('codeOne') codeOne!: ElementRef;
  @ViewChild('codeTwo') codeTwo!: ElementRef;
  @ViewChild('codeThree') codeThree!: ElementRef;
  @ViewChild('codeFour') codeFour!: ElementRef;
  @ViewChild('codeFive') codeFive!: ElementRef;
  @ViewChild('codeSix') codeSix!: ElementRef;

  personalInfo = {
    first_name: '',
    last_name: '',
    country: '',
    state_region: '',
    email: '',
    phone: ''
  };

  CodeData = {
    one: '',
    two: '',
    three: '',
    four: '',
    five: '',
    six: ''
  };

  error_message: string = '';
  phoneIsHidden: boolean = true;
  phoneStartWith: number = 0;
  maskedPhone: string = '';
  rawPhone: string = '';
  lastInputWasPaste = false;
  showSignatureComponent = false;

  expected_email: string = "";
  auth_client_token: any = null;
  isOpenModal: boolean = false;
  isSendedCode: boolean = false;
  isSendCodeDisabled: boolean = false;
  countdown: number = 0;
  countdownInterval: any;

  constructor(
    private countryService: CountryService,
    private formDataService: FormDataService,
    private auth_service: AuthServiceService,
    private client_service: ClientServiceService,
    private translate: TranslateService,
    private router: Router
  ) {}
  
  ngOnInit(): void {
    this.personalInfo.email = this.client_service.existing_form_email;
    this.auth_client_token = localStorage.getItem("auth_client_token");
    if (!this.auth_client_token) {
      this.router.navigate(["/login"]);
    }
    this.check_filling_possibility();
    this.countryService.getCountries().subscribe({
      next: (data) => {
        this.countries = data;
        this.personalInfo.country = this.countries[0].countryName;
        this.selectedCountryRegions = this.countries[0].regions;
        this.phoneStartWith = this.countries[0].phoneStartWith;
        this.currentCountryCode = this.countries[0].countryShortCode;
      },
      error: (err) => {
        // console.error(err);
      }
    });
    this.client_service.get_customer_info(this.auth_client_token).subscribe({
      next: (data) => {
        this.expected_email = data.email;
      },
      error: (err) => {
        // console.log(err);
      }
    })
  }

  getErrorMessage(key: string, params?: any): string {
    return this.translate.instant(`personalInfoErrors.${key}`, params || {});
  }

  check_filling_possibility() {
    this.client_service.check_possibility_filling(this.auth_client_token).subscribe({
      next: (data) => {
        if (!data.can_fill) {
          this.router.navigate(["/dashboard"]);
          window.location.reload();
        }
      },
      error: (err) => {
        this.router.navigate(["/dashboard"]);
        window.location.reload();
      }
    })
  }

  toggleShowPhone() {
    this.phoneIsHidden = !this.phoneIsHidden;
    if (this.phoneIsHidden) {
      this.maskedPhone = this.maskPhoneNumber(this.personalInfo.phone);
    } else {
      this.maskedPhone = this.personalInfo.phone;
    }
  }

  onCountryChange(): void {
    const selectedCountry = this.countries.find(
      (country) => country.countryName === this.personalInfo.country
    );
    if (selectedCountry) {
      this.currentCountryCode = selectedCountry.countryShortCode;
    }
    this.selectedCountryRegions = selectedCountry ? selectedCountry.regions : [];
    this.phoneStartWith = selectedCountry ? selectedCountry.phoneStartWith : null;
    this.personalInfo.state_region = '';
    this.maskedPhone = '';
    this.rawPhone = '';
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
  
    const cleanedInput = value.replace(/[^0-9+]/g, '');

    if (this.lastInputWasPaste) {
      this.rawPhone = cleanedInput;
      this.lastInputWasPaste = false;
    } else {
      const lastChar = cleanedInput.charAt(cleanedInput.length - 1);
      if (/[0-9+]/.test(lastChar)) {
        this.rawPhone += lastChar;
      }
    }
  
    this.updatePhoneStates();
  
    input.value = this.maskedPhone;
  }
  
  onPhonePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const cleaned = pasted.replace(/[^0-9+]/g, '');
    this.rawPhone = cleaned;
    this.lastInputWasPaste = true;
    this.updatePhoneStates();
  }
  
  onPhoneKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Backspace') {
      event.preventDefault();
      this.rawPhone = this.rawPhone.slice(0, -1);
      this.updatePhoneStates();
    }
  }
  
  updatePhoneStates(): void {
    this.maskedPhone = this.phoneIsHidden ? this.maskPhoneNumber(this.rawPhone) : this.rawPhone;
    this.personalInfo.phone = this.rawPhone;
  }
  
  maskPhoneNumber(phone: string): string {
    if (this.error_message === error_message_types.phone_invalid) {
      this.error_message = "";
    }
    if (phone.length < 3) return phone;
    if (phone.length === 3) return phone.slice(0, 2) + '*';
    if (phone.length <= 6) return phone.slice(0, 2) + '*'.repeat(phone.length - 2);
    const first = phone.slice(0, 2);
    const last = phone.slice(6);
    const masked = '****';
    return first + masked + last;
  }
  
  isStateRegionValid(): boolean {
    return this.selectedCountryRegions.length === 0 || 
           (this.selectedCountryRegions.length > 0 && !!this.personalInfo.state_region?.trim());
  }

  resetData() {
    this.personalInfo = {
      first_name: '',
      last_name: '',
      country: this.countries[0].countryName,
      state_region: '',
      email: '',
      phone: ''
    }
    this.selectedCountryRegions = this.countries[0].regions;
  }

  async onSubmit() {
    await this.validatePhoneNumber();
    await this.validatePersonalEmail();
    if (this.error_message) {
      return;
    }
    
    if(this.personalInfo.email !== this.expected_email) {
      this.isOpenModal = true;
      this.sendCodeOnEmail();
    } else {
      this.personalInfo.phone = this.phoneStartWith === 0 ? this.personalInfo.phone : this.phoneStartWith.toString() + this.personalInfo.phone
      this.formDataService.saveFormData(this.personalInfo);
      this.showSignatureComponent = true;
      this.infoSubmit.emit();
    }
  }

  async validatePhoneNumber() {
    return new Promise((resolve, reject) => {
      const validPhone = this.phoneStartWith === 0 ? this.personalInfo.phone : this.phoneStartWith + this.personalInfo.phone;
      this.formDataService.validate_phone_number(validPhone).subscribe({
        next: (data) => {
          if (!data.valid) {
            this.error_message = this.getErrorMessage('phoneInvalid');
          } else {
            this.error_message = "";
          }
          return resolve(data);
        },
        error: (err) => {
          return reject(err);
        }
      })
    })
  }

  async validatePersonalEmail(): Promise<{ valid: boolean; message?: string }> {
    return new Promise((resolve) => {
      this.client_service.get_auth_form_by_email(this.auth_client_token, this.personalInfo.email).subscribe({
        next: (data) => {
          if (!this.error_message){
            this.error_message = this.getErrorMessage('emailAlreadyExists', { email: this.personalInfo.email });
          }
          resolve({ valid: false, message: this.error_message });
        },
        error: (err) => {
          if (err.status === 404) {
            if (!this.error_message) {
              this.error_message = "";
            }
            resolve({ valid: true });
          } else {
            this.error_message = this.getErrorMessage('emailValidationError');
            resolve({ valid: false, message: this.error_message });
          }
        }
      });
    });
  }

  onSignatureSubmit(): void {
    this.signatureSubmit.emit();
  }

  onRerender(): void {
    this.rerender.emit();
  }

  onEmailChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.personalInfo.email = input.value.toLowerCase();
    // if (this.error_message === error_message_types.email_invalid(this.personalInfo.email)) {
    //   this.error_message = "";
    // }
    if (this.error_message === this.getErrorMessage('emailAlreadyExists', { email: this.personalInfo.email })) {
      this.error_message = "";
    }
  }

  onCodeInput(event: Event, field: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.error_message = "";

    if (field === 'one' && value.length === 6 && /^\d{6}$/.test(value)) {
      const digits = value.split('');
      this.CodeData.one = digits[0];
      this.CodeData.two = digits[1];
      this.CodeData.three = digits[2];
      this.CodeData.four = digits[3];
      this.CodeData.five = digits[4];
      this.CodeData.six = digits[5];
      this.codeSix.nativeElement.focus();
      return;
    }

    if (value.length === 1 && field !== 'six') {
      const nextFieldMap: { [key: string]: string } = {
        one: 'two',
        two: 'three',
        three: 'four',
        four: 'five',
        five: 'six'
      };
      const nextField = nextFieldMap[field];
      const nextInput = this[`code${nextField.charAt(0).toUpperCase() + nextField.slice(1)}` as keyof this] as ElementRef;
      nextInput.nativeElement.focus();
    }
  }

  onCodeKeydown(event: KeyboardEvent, field: string): void {
    const input = event.target as HTMLInputElement;
    this.error_message = "";
    
    if (event.key === 'Backspace') {
      if (!input.value && field !== 'one') {
        const prevFieldMap: { [key: string]: string } = {
          two: 'one',
          three: 'two',
          four: 'three',
          five: 'four',
          six: 'five'
        };
        const prevField = prevFieldMap[field];
        this.CodeData[field as keyof typeof this.CodeData] = '';
        const prevInput = this[`code${prevField.charAt(0).toUpperCase() + prevField.slice(1)}` as keyof this] as ElementRef;
        prevInput.nativeElement.focus();
        event.preventDefault();
      } else if (input.value) {
        this.CodeData[field as keyof typeof this.CodeData] = '';
        event.preventDefault();
      }
    }

    if (event.key === 'ArrowRight' && field !== 'six') {
      const nextFieldMap: { [key: string]: string } = {
        one: 'two',
        two: 'three',
        three: 'four',
        four: 'five',
        five: 'six'
      };
      const nextField = nextFieldMap[field];
      const nextInput = this[`code${nextField.charAt(0).toUpperCase() + nextField.slice(1)}` as keyof this] as ElementRef;
      nextInput.nativeElement.focus();
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' && field !== 'one') {
      const prevFieldMap: { [key: string]: string } = {
        two: 'one',
        three: 'two',
        four: 'three',
        five: 'four',
        six: 'five'
      };
      const prevField = prevFieldMap[field];
      const prevInput = this[`code${prevField.charAt(0).toUpperCase() + prevField.slice(1)}` as keyof this] as ElementRef;
      prevInput.nativeElement.focus();
      event.preventDefault();
    }
  }

  startCountdown(seconds: number = 60) {
    this.isSendCodeDisabled = true;
    this.countdown = seconds;

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.isSendCodeDisabled = false;
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  clearCode() {
    this.CodeData = {
      one: '',
      two: '',
      three: '',
      four: '',
      five: '',
      six: ''
    }
  }

  sendCodeOnEmail() {
    this.auth_service.send_verification_code(this.personalInfo.email, this.auth_client_token).subscribe({
      next: (data) => {
        this.isSendedCode = true;
        this.error_message = "";
        this.clearCode();
        this.startCountdown();
      },
      error: (err) => {
        this.isSendedCode = true;
        this.startCountdown();
        if (err.status === 400) {
          this.error_message = err.error?.detail;
        } else {
          this.error_message = 'An error occurred. Please try again';
        }
      }
    })
  }

  isCodeValid(): boolean {
    return (
      !!this.CodeData.one &&
      !!this.CodeData.two &&
      !!this.CodeData.three &&
      !!this.CodeData.four &&
      !!this.CodeData.five &&
      !!this.CodeData.six
    );
  }

  verify() {
    this.error_message = '';

    const email = this.personalInfo.email;
    const code =
      this.CodeData.one +
      this.CodeData.two +
      this.CodeData.three +
      this.CodeData.four +
      this.CodeData.five +
      this.CodeData.six;
    
    this.auth_service.verify_email_in_auth_form(email, code, this.auth_client_token).subscribe({
      next: (data) => {
        this.personalInfo.phone = this.phoneStartWith === 0 ? this.personalInfo.phone : this.phoneStartWith.toString() + this.personalInfo.phone
        this.formDataService.saveFormData(this.personalInfo);
        this.showSignatureComponent = true;
        this.infoSubmit.emit();
      },
      error: (err) => {
        if (err.status === 400) {
          if (err.error?.detail === "Invalid OTP entered") {
            this.error_message = this.getErrorMessage('codeIncorrect');
          } else {
            this.error_message = this.getErrorMessage('codeInvalidOrExpired');
          }
        } else {
          this.error_message = 'An error occurred. Please try again';
        }
      }
    })
  }

  closeModal() {
    this.isOpenModal = false;
    this.error_message = "";
  }
}